import { T, useGT } from 'gt-react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import {
  validateCsvParseOptions,
  type CsvParseOptions,
  type CsvParseOptionsErrors,
  type CsvParseOptionsField,
} from '@/lib/csv-parse-options';

/** Translated copy for why a field is unusable. */
function useErrorText(): (
  field: CsvParseOptionsField,
  errors: CsvParseOptionsErrors,
) => string | undefined {
  const gt = useGT();

  return function errorText(field, errors) {
    switch (errors[field]) {
      case undefined:
        return undefined;
      case 'empty':
        return gt('Enter a character, or use \\t for a tab.', {
          context: 'validation message when the CSV delimiter field is empty',
        });
      case 'too-long':
        return gt('Use a single character.', {
          context:
            'validation message when a CSV delimiter or quote field has more than one character',
        });
      case 'forbidden':
        return gt('A line break or a double quote cannot separate fields.', {
          context:
            'validation message when the CSV delimiter is a character the parser cannot use',
        });
    }
  };
}

/** A one-character field with its own validation message. */
function CharField({
  id,
  label,
  hint,
  value,
  error,
  disabled,
  onChange,
}: {
  id: string;
  label: React.ReactNode;
  hint: React.ReactNode;
  value: string;
  error: string | undefined;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <Label htmlFor={id} className="text-xs">
        {label}
      </Label>
      <Input
        id={id}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        // Room for `\t` plus a stray keystroke, so an over-long value is still
        // reachable and gets a message rather than being silently truncated.
        maxLength={4}
        className={cn('h-8 font-mono', error && 'border-destructive')}
      />
      <p
        className={cn(
          'text-xs',
          error ? 'text-destructive' : 'text-muted-foreground',
        )}
      >
        {error ?? hint}
      </p>
    </div>
  );
}

interface CsvParseOptionsSectionProps {
  options: CsvParseOptions;
  onChange: (options: CsvParseOptions) => void;
  /** Locks the controls while a read is in flight. */
  disabled?: boolean;
  /** Distinguishes the input ids when two of these appear on one page. */
  idPrefix?: string;
}

/**
 * Delimiter and quote-character overrides for a CSV read.
 *
 * Auto-detect is on by default and covers ordinary comma-separated files, so the
 * fields stay out of the way until someone has a file that needs them — a
 * tab-separated export, or one where quote characters are data rather than
 * quoting.
 */
export function CsvParseOptionsSection({
  options,
  onChange,
  disabled = false,
  idPrefix = 'csv',
}: CsvParseOptionsSectionProps) {
  const errorText = useErrorText();
  const errors = validateCsvParseOptions(options);
  const fieldsDisabled = disabled || options.autoDetect;

  return (
    <div className="flex flex-col gap-3 rounded-lg border p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <Label htmlFor={`${idPrefix}-auto-detect`} className="text-sm">
            <T context="label for the toggle that auto-detects a CSV's delimiter and quoting">
              Detect delimiter and quoting automatically
            </T>
          </Label>
          <p className="text-xs text-muted-foreground">
            <T context="explanation of the CSV auto-detect toggle">
              Leave this on for ordinary comma-separated files. Turn it off to
              set the characters yourself.
            </T>
          </p>
        </div>
        <Switch
          id={`${idPrefix}-auto-detect`}
          checked={options.autoDetect}
          disabled={disabled}
          onCheckedChange={(autoDetect) => onChange({ ...options, autoDetect })}
        />
      </div>

      {!options.autoDetect && (
        <div className="flex flex-col gap-3 sm:flex-row">
          <CharField
            id={`${idPrefix}-delimiter`}
            label={
              <T context="label for the CSV field separator input">Delimiter</T>
            }
            hint={
              <T context="hint under the CSV delimiter input, naming the tab escape">
                One character. Use \t for a tab.
              </T>
            }
            value={options.delimiter}
            error={errorText('delimiter', errors)}
            disabled={fieldsDisabled}
            onChange={(delimiter) => onChange({ ...options, delimiter })}
          />
          <CharField
            id={`${idPrefix}-quote`}
            label={
              <T context="label for the CSV quote character input">
                Quote character
              </T>
            }
            hint={
              <T context="hint under the CSV quote character input, explaining that blank disables quoting">
                Leave blank if the file has no quoting.
              </T>
            }
            value={options.quoteChar}
            error={errorText('quoteChar', errors)}
            disabled={fieldsDisabled}
            onChange={(quoteChar) => onChange({ ...options, quoteChar })}
          />
        </div>
      )}
    </div>
  );
}
